import { RcFile } from 'antd/lib/upload/interface';
import { TagType } from 'utils/types/enums';
import { pimsApi } from './pimsApi';

interface PatientS3File {
	s3_file_id: number;
	s3_path: string;
	created_by: string;
	created_at: number;
}

// Define a service using a base URL and expected endpoints
export const filesService = pimsApi.injectEndpoints({
	endpoints: (builder) => ({
		uploadNoteFileDirect: builder.mutation<
			null,
			{
				visitId: number;
				noteId: number;
				body: {
					file_name: string;
					file_obj: RcFile;
				};
			}
		>({
			query: ({ visitId, noteId, body }) => {
				const { file_name, file_obj } = body;
				const formData = new FormData();
				formData.append('file_name', file_name);
				formData.append('file_obj', file_obj);
				// formData.append('file_obj', file_obj)
				return {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					url: `visit/${visitId}/note/${noteId}/file/upload`,
					method: 'POST',
					body: formData,
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.ProgNote, id: visitId },
				{ type: TagType.NoteFile, id: visitId },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),
		// todo see if we can impliment this, currently blocked by a CORS issue
		// uploadNoteFilePresigned: builder.mutation<
		// 	null,
		// 	{
		// 		visitId: number;
		// 		noteId: number;
		// 		body: {
		// 			file_name: string;
		// 			file_obj: RcFile;
		// 		};
		// 	}
		// >({
		// 	async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
		// 		const { file_name, file_obj } = _arg.body;

		// 		const getPresignedPostRequest = await fetchWithBQ({
		// 			url: `/visit/${_arg.visitId}/note/${_arg.noteId}/file/presignpost`,
		// 			method: 'POST',
		// 			body: { file_name },
		// 		});
		// 		if (getPresignedPostRequest.error) {
		// 			throw getPresignedPostRequest.error;
		// 		}
		// 		const presignedUrlData = (getPresignedPostRequest?.data as any)
		// 			?.data;
		// 		if (presignedUrlData?.fields && presignedUrlData?.url) {
		// 			const formData = new FormData();
		// 			Object.keys(presignedUrlData?.fields).forEach((key) =>
		// 				formData.append(key, presignedUrlData?.fields?.[key]),
		// 			);
		// 			formData.append('file', file_obj);
		// 			debugger;
		// 			const s3Upload = await fetchWithBQ({
		// 				url: presignedUrlData.url,
		// 				method: 'POST',
		// 				body: formData,
		// 				headers: {
		// 					'Content-Type': 'multipart/form-data',
		// 				},
		// 			});
		// 			debugger;
		// 			if (s3Upload.error) {
		// 				throw s3Upload.error;
		// 			}
		// 		}

		// 		return { data: null };
		// 	},
		// 	invalidatesTags: (_, __, { visitId }) => [
		// 		{ type: TagType.ProgNote, id: visitId },
		// 		{ type: TagType.NoteFile, id: visitId },
		// 	],
		// }),

		deleteNoteFile: builder.mutation<
			null,
			{
				visitId: number;
				noteId: number;
			}
		>({
			query: ({ visitId, noteId }) => {
				return {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					url: `visit/${visitId}/note/${noteId}/file`,
					method: 'Delete',
					body: {},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.ProgNote, id: visitId },
				{ type: TagType.NoteFile, id: visitId },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),
		getNoteFileURL: builder.query<
			string,
			{ visitId: number; noteId: number }
		>({
			query: ({ visitId, noteId }) => `visit/${visitId}/file/${noteId}`,
			providesTags: (result, error, { visitId }) => [
				{ type: TagType.NoteFile, id: visitId },
			],

			transformResponse: (response: { data: string }) => {
				return response.data;
			},
		}),
		getPatientFiles: builder.query<PatientS3File[], { pet_id: string }>({
			query: ({ pet_id }) => `pet/${pet_id}/files`,
			providesTags: (result, error, { pet_id }) => [
				{ type: TagType.PatientFile, pet_id },
			],
			transformResponse: (response: { data: PatientS3File[] }) => {
				return response.data;
			},
		}),
		getFileLink: builder.query<string, { pet_id: string; file_id: number }>(
			{
				query: ({ pet_id, file_id }) => `pet/${pet_id}/file/${file_id}`,
				transformResponse: (response: { data: string }) => {
					return response.data;
				},
			},
		),
		deletePatientFile: builder.mutation<
			null,
			{ pet_id: string; file_id: number }
		>({
			query: ({ pet_id, file_id }) => ({
				method: 'Delete',
				url: `pet/${pet_id}/file/${file_id}`,
			}),
			invalidatesTags: (result, error, { pet_id }) => [
				{ type: TagType.PatientFile, pet_id },
			],
		}),
		uploadPatientFile: builder.mutation<
			null,
			{
				pet_id: string;
				file_name: string;
				file_obj: File;
			}
		>({
			query: ({ pet_id, file_name, file_obj }) => {
				const formData = new FormData();
				formData.append('file_name', file_name);
				formData.append('file_obj', file_obj);

				return {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					url: `/pet/${pet_id}/file/upload`,
					method: 'POST',
					body: formData,
				};
			},
			invalidatesTags: (result, error, { pet_id }) => [
				{ type: TagType.PatientFile, pet_id },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
	useUploadNoteFileDirectMutation,
	useGetNoteFileURLQuery,
	useDeleteNoteFileMutation,
	useGetPatientFilesQuery,
	useLazyGetFileLinkQuery,
	useDeletePatientFileMutation,
	useUploadPatientFileMutation,
} = filesService;
